import { useEffect, type ReactNode } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/api/firebase";
import { useAuthStore } from "@/stores/auth-store";
import type { OrgRole } from "@/lib/permissions";

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setUser, clearUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Dev-only: auto-login from URL query param (?devLogin=admin)
    if (import.meta.env.DEV) {
      const params = new URLSearchParams(window.location.search);
      const alias = params.get("devLogin");
      if (alias) {
        const accounts: Record<string, { email: string; password: string }> = {
          admin:    { email: "admin@test.medtechos.dev",    password: "Test123!" },
          dm:       { email: "dm@test.medtechos.dev",       password: "Test123!" },
          viewer:   { email: "viewer@test.medtechos.dev",   password: "Test123!" },
          newadmin: { email: "newadmin@test.medtechos.dev",  password: "Test123!" },
        };
        const creds = accounts[alias];
        if (creds) {
          signInWithEmailAndPassword(auth, creds.email, creds.password)
            .then(() => {
              const url = new URL(window.location.href);
              url.searchParams.delete("devLogin");
              window.history.replaceState({}, "", url.toString());
            })
            .catch((err) => console.error("Dev login failed:", err));
        }
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        const claims = tokenResult.claims;
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          orgId: (claims.orgId as string) || "default",
          role: (claims.role as OrgRole) || "viewer",
          isPlatformAdmin: !!(claims.platformAdmin || claims.admin),
        });
      } else {
        clearUser();
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [setUser, clearUser, setLoading]);

  return <>{children}</>;
}
